import { useState } from 'react';
import { supabase } from '../../../lib/supabase';

interface DiscoverHeaderProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onSubmitClick: () => void;
  onCsvImportClick: () => void;
  onDeduplicateComplete: () => void;
  bulkSelectMode?: boolean;
  onToggleBulkSelect?: () => void;
}

interface DuplicateGroup {
  name: string;
  products: { id: string; name: string; rating: number; created_at: string }[];
}

export function DiscoverHeader({
  searchQuery,
  onSearchChange,
  onSubmitClick,
  onCsvImportClick,
  onDeduplicateComplete,
  bulkSelectMode = false,
  onToggleBulkSelect,
}: DiscoverHeaderProps) {
  const [showDedupeModal, setShowDedupeModal] = useState(false);
  const [duplicates, setDuplicates] = useState<DuplicateGroup[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeResult, setRemoveResult] = useState<{ removed: number } | null>(null);

  const handleScanDuplicates = async () => {
    setShowDedupeModal(true);
    setIsScanning(true);
    setRemoveResult(null);

    try {
      const { data, error } = await supabase
        .from('products')
        .select('id, name, rating, created_at')
        .eq('status', 'approved')
        .order('created_at', { ascending: true });

      if (error) throw error;

      const nameMap = new Map<
        string,
        { id: string; name: string; rating: number; created_at: string }[]
      >();
      (data || []).forEach((p: any) => {
        const key = p.name.toLowerCase().trim();
        if (!nameMap.has(key)) nameMap.set(key, []);
        nameMap.get(key)!.push(p);
      });

      const dupes: DuplicateGroup[] = [];
      nameMap.forEach((products, key) => {
        if (products.length > 1) {
          dupes.push({ name: products[0].name, products });
        }
      });

      setDuplicates(dupes);
    } catch (err) {
      console.error('Error scanning duplicates:', err);
    } finally {
      setIsScanning(false);
    }
  };

  const handleRemoveAllDuplicates = async () => {
    setIsRemoving(true);
    let removed = 0;

    try {
      for (const group of duplicates) {
        // Keep the first (oldest) entry, delete the rest
        const idsToDelete = group.products.slice(1).map((p) => p.id);
        if (idsToDelete.length === 0) continue;

        const { error } = await supabase.from('products').delete().in('id', idsToDelete);

        if (!error) {
          removed += idsToDelete.length;
        } else {
          console.error('Supabase delete error:', error);
        }
      }

      setRemoveResult({ removed });
      setDuplicates([]);
      onDeduplicateComplete();
    } catch (err) {
      console.error('Error removing duplicates:', err);
    } finally {
      setIsRemoving(false);
    }
  };

  const handleRemoveSingle = async (groupIndex: number, productId: string) => {
    try {
      const { error } = await supabase.from('products').delete().eq('id', productId);

      if (!error) {
        setDuplicates((prev) => {
          const updated = [...prev];
          const group = { ...updated[groupIndex] };
          group.products = group.products.filter((p) => p.id !== productId);
          if (group.products.length <= 1) {
            updated.splice(groupIndex, 1);
          } else {
            updated[groupIndex] = group;
          }
          return updated;
        });
        onDeduplicateComplete();
      } else {
        console.error('Supabase delete error:', error);
      }
    } catch (err) {
      console.error('Error removing product:', err);
    }
  };

  return (
    <>
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Discover AGI</h1>
              <p className="text-gray-600">Explore the latest AI products and tools</p>
            </div>
            <div className="flex gap-3">
              {onToggleBulkSelect && (
                <button
                  onClick={onToggleBulkSelect}
                  className={`px-5 py-2.5 border rounded-lg font-medium transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                    bulkSelectMode
                      ? 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100'
                      : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <i className={`${bulkSelectMode ? 'ri-close-line' : 'ri-checkbox-multiple-line'} text-lg`}></i>
                  {bulkSelectMode ? 'Exit Bulk Delete' : 'Bulk Delete'}
                </button>
              )}
              <button
                onClick={handleScanDuplicates}
                className="px-5 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-file-copy-line text-lg"></i>
                Deduplicate
              </button>
              <button
                onClick={onCsvImportClick}
                className="px-5 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-file-text-line text-lg"></i>
                Import CSV
              </button>
              <button
                onClick={onSubmitClick}
                className="px-5 py-2.5 bg-teal-500 text-white rounded-lg font-medium hover:bg-teal-600 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-add-line text-lg"></i>
                Submit Product
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Deduplicate Modal */}
      {showDedupeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-orange-50 rounded-lg">
                  <i className="ri-file-copy-line text-orange-600 text-lg"></i>
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Duplicate Check</h2>
                  <p className="text-xs text-gray-500">Find and remove duplicate products by name</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowDedupeModal(false);
                  setRemoveResult(null);
                }}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer rounded-lg hover:bg-gray-100"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {isScanning ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                  <p className="text-sm text-gray-600">Scanning for duplicates...</p>
                </div>
              ) : removeResult ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-16 h-16 flex items-center justify-center bg-green-50 rounded-full mb-4">
                    <i className="ri-check-double-line text-green-500 text-3xl"></i>
                  </div>
                  <p className="text-lg font-bold text-gray-900">Cleanup Complete!</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Removed{' '}
                    <strong className="text-teal-600">{removeResult.removed}</strong> duplicate
                    {removeResult.removed !== 1 ? 's' : ''}
                  </p>
                </div>
              ) : duplicates.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-16 h-16 flex items-center justify-center bg-green-50 rounded-full mb-4">
                    <i className="ri-shield-check-line text-green-500 text-3xl"></i>
                  </div>
                  <p className="text-lg font-bold text-gray-900">No Duplicates Found</p>
                  <p className="text-sm text-gray-500 mt-1">Your product database is clean!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-700">
                      Found{' '}
                      <strong className="text-orange-600">{duplicates.length}</strong> group
                      {duplicates.length !== 1 ? 's' : ''} with duplicates
                    </p>
                    <button
                      onClick={handleRemoveAllDuplicates}
                      disabled={isRemoving}
                      className="px-4 py-2 bg-red-500 text-white text-xs font-semibold rounded-lg hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center gap-1.5"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                      {isRemoving ? 'Removing...' : 'Remove All Duplicates'}
                    </button>
                  </div>

                  {duplicates.map((group, gi) => (
                    <div key={gi} className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2.5 flex items-center gap-2">
                        <i className="ri-file-copy-line text-orange-500 text-sm"></i>
                        <span className="text-sm font-semibold text-gray-800">{group.name}</span>
                        <span className="text-[10px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-semibold">
                          {group.products.length} entries
                        </span>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {group.products.map((p, pi) => (
                          <div key={p.id} className="px-4 py-3 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {pi === 0 ? (
                                <span className="px-2 py-0.5 bg-green-50 text-green-700 text-[10px] font-bold rounded-full whitespace-nowrap">
                                  KEEP
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 bg-red-50 text-red-600 text-[10px] font-bold rounded-full whitespace-nowrap">
                                  DUPLICATE
                                </span>
                              )}
                              <div>
                                <p className="text-xs font-medium text-gray-800">{p.name}</p>
                                <p className="text-[10px] text-gray-400 mt-0.5">
                                  Rating: {p.rating} &middot; Added:{' '}
                                  {new Date(p.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            {pi > 0 && (
                              <button
                                onClick={() => handleRemoveSingle(gi, p.id)}
                                className="px-3 py-1.5 text-[11px] font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                              >
                                <i className="ri-delete-bin-line text-xs"></i>
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-100 px-6 py-4 flex-shrink-0">
              <button
                onClick={() => {
                  setShowDedupeModal(false);
                  setRemoveResult(null);
                }}
                className="w-full px-5 py-2.5 border border-gray-200 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
